import clsx from "clsx";
import { GripVertical, Plus, RotateCcw, Undo2 } from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";

import type { CompositionSide, CompositionUnit, DeployedUnit, Phase } from "../../api/types";
import { designate } from "../../components/attention";
import { DisclosureRow, PanelRow } from "../../components/panel";
import { Motion, Presence } from "../../components/presence";
import { Badge, Button } from "../../components/ui";
import { unitDesignation } from "../../i18n/designation";
import { t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { symbolSvg } from "../../map/units/symbols";
import { useDeployment } from "../../map/units/useDeployment";
import { COMMAND_LABELS, isCompany } from "../../company";
import { useGenericEnemy } from "../../enemyDisplay";
import { useUi } from "../../state/ui";
import { useLeavingList } from "../leavingList";
import { LIST_DRAG_ZONE, useListDrag } from "../useListDrag";
import { AddUnitDialog } from "./AddUnitDialog";
import { UnitRowMenu } from "./RoleMenu";
import { defensiveRoles } from "../../map/units/propagation";
import { defaultMark, opoMention, roleShort, roleText } from "./roles";
import { compositionRows, type SideView, sideView } from "./rows";
import { useUnitActions } from "./useUnitActions";

/** Unités du dossier d'un camp (docs/18 §6.1-6.2 ; généralise la liste de l'ordre de bataille ennemi, docs/14 §7.10b) :
 *  celles qui sont sur la carte (un clic les localise) et, à leur rang, celles à glisser sur la carte depuis la liste
 *  — à placer (ajoutées, en surnombre, rôle refusé par l'emplacement) ou posées depuis la liste à un temps ultérieur —,
 *  à la souris par toute la ligne, au doigt par la poignée. Le menu de chaque unité change son rôle dans ce dossier
 *  (avec sa correspondance dans l'OPO), la pose au centre de la carte, la retire de la carte ou du dossier. */
export function DossierUnits({
  side,
  onLocate,
  placeableOnly = false,
}: {
  side: CompositionSide;
  onLocate: (unit: DeployedUnit, phase: Phase) => void;
  /** Seulement les unités à placer (l'appelant montre déjà les autres). */
  placeableOnly?: boolean;
}) {
  const { data: deployment } = useDeployment();
  const selection = useUi((s) => s.selection);
  const notify = useUi((s) => s.notify);
  const actions = useUnitActions();
  const { writes, phase } = actions;
  const [menu, setMenu] = useState<string | null>(null);

  const drag = useListDrag<{ id: string; label: string; sidc: string }>({
    canEdit: writes.canEdit && !actions.pendingZone,
    refuseNotSaved: actions.refuse,
    drop: (item, at) => actions.place(item.id, at),
    // Simple clic : l'unité se glisse, le dire (la poignée est désignée).
    click: (row) => {
      designate(row.querySelector<HTMLElement>("[data-drag-handle]"), "ring");
      notify({ text: t("Glissez l'unité sur la carte pour la placer en {phase}", { phase }), tone: "info" });
    },
  });

  const view = sideView(deployment, side);
  const deployed = (unit: CompositionUnit) => deployment?.units.find((u) => u.key === unit.key);
  const rows = compositionRows(view, (key) => Boolean(deployment?.units.find((u) => u.key === key)?.positions[phase]), placeableOnly);
  if (!view || rows.length === 0) return null;

  const top = Math.min(...rows.map((r) => r.unit.depth));
  const odbName = side === "ENI" && view.legacy ? deployment?.eni_odb?.name : null;
  return (
    <div {...LIST_DRAG_ZONE}>
      <p className="px-2 pt-1 pb-1.5 text-meta leading-snug text-ink-muted">
        {placeableOnly
          ? t(
              rows.length > 1
                ? "{units} à placer : glissez-les sur la carte pour les poser en {phase}."
                : "{units} à placer : glissez-la sur la carte pour la poser en {phase}.",
              {
                units:
                  side === "AMI"
                    ? plural(rows.length, t("unité amie"), t("unités amies"))
                    : plural(rows.length, t("unité ennemie"), t("unités ennemies")),
                phase,
              },
            )
          : odbName
            ? t("Ordre de bataille ennemi « {name} » : glissez une unité non placée sur la carte pour la poser en {phase}.", {
                name: odbName,
                phase,
              })
            : t("Glissez une unité à placer sur la carte pour la poser en {phase}.", { phase })}
      </p>
      <ul className="space-y-px pb-1">
        {rows.map(({ unit, placeable, draggable }) => {
          const onMap = deployed(unit);
          const position = onMap?.positions[phase];
          // Ami : la désignation (« 4e Cie/2RIMa ») et non le code interne de l'ODB ; ennemi : son code iPad.
          // Interface en anglais : désignations OTAN (« 1erRCP » → « 1 Para Inf Regt »), clés inchangées.
          const title = unitDesignation(side === "AMI" ? unit.label : unit.code);
          const name = side === "ENI" && unit.label !== unit.code ? unitDesignation(unit.label) : null;
          const numbers =
            onMap && (onMap.designation_left || onMap.designation_right)
              ? `${unitDesignation(onMap.designation_left) || "–"} ⁄ ${unitDesignation(onMap.designation_right) || "–"}`
              : null;
          const detail = [
            numbers,
            name,
            roleText(unit, isCompany(deployment?.level) && side === "AMI", defensiveRoles(deployment)),
            placeable
              ? t("À placer")
              : position
                ? position.locality || t("Lieu-dit sans nom")
                : draggable
                  ? t("Absente de la carte en {phase} : glissez-la pour l'y ajouter", { phase })
                  : t("Absente de la carte en {phase}", { phase }),
          ]
            .filter(Boolean)
            .join(" · ");
          const dragging = drag.dragging === unit.key;
          const handlers = draggable ? drag.bind({ id: unit.key, label: unit.label, sidc: unit.sidc }) : undefined;
          return (
            <li key={unit.id} style={{ paddingLeft: (unit.depth - top) * 12 }}>
              <div
                data-composition-unit={unit.id}
                data-eni-unit={side === "ENI" ? unit.code : undefined}
                data-no-press={draggable ? "" : undefined}
                className={clsx(
                  draggable && "cursor-grab select-none",
                  dragging && "opacity-40",
                  "rounded-lg transition-opacity duration-(--gx-enter)",
                )}
                {...handlers}
              >
                <PanelRow
                  icon={
                    <span
                      className="flex h-4 w-6 items-center justify-center [&>svg]:max-h-4 [&>svg]:max-w-6"
                      dangerouslySetInnerHTML={{ __html: symbolSvg(unit.sidc, 13) }}
                    />
                  }
                  title={<span className={clsx(side === "ENI" && "font-mono", "tabular-nums")}>{title}</span>}
                  detail={detail}
                  state={<UnitBadges unit={unit} />}
                  selected={Boolean(onMap) && selection?.layer === "unit" && selection.code === onMap?.code}
                  onClick={onMap && position ? () => onLocate(onMap, phase) : undefined}
                  clickTitle={onMap && position ? t("Localiser l'unité en {phase}", { phase }) : undefined}
                  actions={
                    <>
                      <UnitRowMenu
                        view={view}
                        unit={unit}
                        open={menu === unit.id}
                        onOpenChange={(open) => setMenu(open ? unit.id : null)}
                        busy={writes.busy}
                        draggable={draggable}
                        onRole={(role) => actions.guard(() => writes.setRole.mutate({ side, unit: unit.id, role }))}
                        onPlaceCenter={() => actions.placeAtCenter(unit.key)}
                        onUnplace={() => actions.guard(() => writes.unplace.mutate(unit.key))}
                        onRemove={() => actions.guard(() => writes.compose.mutate({ op: "remove", side, key: unit.id }))}
                        onCommand={(command) =>
                          actions.guard(() => writes.compose.mutate({ op: "command", side: "AMI", key: unit.id, command }))
                        }
                      />
                      {draggable ? (
                        <span
                          data-drag-handle=""
                          className="flex size-6 shrink-0 touch-none items-center justify-center rounded-md text-ink-muted"
                          title={t("Glisser sur la carte")}
                          aria-hidden
                        >
                          <GripVertical size={14} />
                        </span>
                      ) : (
                        <span aria-hidden className="size-6 shrink-0" />
                      )}
                    </>
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>
      {drag.layer}
    </div>
  );
}

/** Pastilles d'une unité du dossier : ajoutée, rôle choisi (la bulle dit le rôle par défaut et le paragraphe de
 *  l'OPO). `compact` : pastilles réduites à leur point, le libellé en bulle (arbre de l'ordre de bataille, où le nom
 *  de l'unité doit rester lisible). */
export function UnitBadges({ unit, compact = false }: { unit: CompositionUnit; compact?: boolean }) {
  const neutral = useGenericEnemy();
  const { data: deployment } = useDeployment();
  // Niveau compagnie (docs/21 §4) : rôles des emplacements du S/GTIA (« Appui », « Pion de tête »).
  const company = isCompany(deployment?.level) && unit.side === "AMI";
  const defensive = unit.side === "AMI" && defensiveRoles(deployment);
  const mention = opoMention(unit, company, defensive);
  const added = t("Ajoutée");
  const chosen = t("Rôle choisi");
  const addedTitle = unit.source_label ? t("Ajoutée depuis {source}", { source: unit.source_label }) : undefined;
  const chosenTitle = [
    unit.role_default
      ? t("{mark} : {role}", { mark: defaultMark(unit, neutral), role: roleShort(unit.role_default, company, defensive) })
      : t("Rôle par défaut : automatique"),
    mention,
  ]
    .filter(Boolean)
    .join("\n");
  const label = (text: string) => (compact ? <span className="sr-only">{text}</span> : text);
  return (
    <>
      {unit.origin === "added" && (
        <Badge dot size="sm" tone="friend" title={compact ? [added, addedTitle].filter(Boolean).join("\n") : addedTitle}>
          {label(added)}
        </Badge>
      )}
      {unit.role_chosen && (
        <Badge dot size="sm" tone="accent" title={compact ? `${chosen}\n${chosenTitle}` : chosenTitle}>
          {label(chosen)}
        </Badge>
      )}
      {/* Unité reçue par un S/GTIA (docs/21, décision 5) : sa relation de commandement, expliquée en bulle. */}
      {unit.command && (
        <Badge size="sm" tone="neutral" title={t(COMMAND_LABELS[unit.command].hint)}>
          {unit.command}
        </Badge>
      )}
    </>
  );
}

/** Volet « Retirées du dossier ({n}) » d'un camp, avec « Rétablir » par ligne ; les lignes rétablies sortent en
 *  fondu. */
export function RemovedUnits({ side }: { side: CompositionSide }) {
  const { data: deployment } = useDeployment();
  const actions = useUnitActions();
  const [open, setOpen] = useState(false);
  const view = sideView(deployment, side);
  const removed = view && !view.legacy ? view.out.removed : [];
  const entries = useLeavingList(removed, (r) => r.id);
  if (!view) return null;
  // Volet entier en fondu : il sort quand la dernière unité est rétablie (annulation comprise), au lieu de rester
  // affiché à « (0) » avec sa ligne sortie.
  return (
    <Presence show={removed.length > 0}>
      <Motion origin="fade" className="pt-1">
        {/* Même ligne repliable que les autres sous-listes (chevron, compteur) : on voit qu'elle se déplie. */}
        <DisclosureRow icon={Undo2} title={t("Retirées du dossier")} count={removed.length} open={open} onToggle={() => setOpen(!open)}>
          <ul className="space-y-px pb-1">
            {entries.map(({ key, item, present }) => (
              <li key={key} data-composition-removed={item.id}>
                <Presence show={present}>
                  <Motion origin="fade">
                    <PanelRow
                      icon={
                        <span
                          className="flex h-4 w-6 items-center justify-center opacity-40 [&>svg]:max-h-4 [&>svg]:max-w-6"
                          dangerouslySetInnerHTML={{ __html: symbolSvg(item.sidc, 13) }}
                        />
                      }
                      title={
                        <span className={clsx(side === "ENI" && "font-mono", "text-ink-muted tabular-nums")}>
                          {unitDesignation(side === "AMI" ? item.label : item.code)}
                        </span>
                      }
                      detail={side === "ENI" && item.label !== item.code ? unitDesignation(item.label) : undefined}
                      actions={
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={actions.writes.busy || !present}
                          blocked={!actions.writes.canEdit || Boolean(actions.pendingZone)}
                          onBlocked={actions.refuse}
                          onClick={() => actions.writes.compose.mutate({ op: "restore", side, key: item.id })}
                        >
                          <RotateCcw size={14} /> {t("Rétablir")}
                        </Button>
                      }
                    />
                  </Motion>
                </Presence>
              </li>
            ))}
          </ul>
        </DisclosureRow>
      </Motion>
    </Presence>
  );
}

/** Bouton « Ajouter une unité… » d'un camp et sa fenêtre. Camp non modifiable (contre-rébellion côté ennemi) : bouton
 *  désactivé, raison en bulle et en ligne. */
export function AddUnitButton({ side, compact = false, className }: { side: CompositionSide; compact?: boolean; className?: string }) {
  const { data: deployment } = useDeployment();
  const actions = useUnitActions();
  const [open, setOpen] = useState(false);
  const view: SideView | null = sideView(deployment, side);
  if (!view || view.legacy) return null;
  const refusal = view.out.editable ? null : view.out.refusal;
  return (
    <>
      <Button
        size={compact ? "sm" : "md"}
        variant="secondary"
        className={className}
        data-attention={`composition-add-${side}`}
        disabled={!view.out.editable}
        title={
          refusal
            ? tServer(refusal)
            : side === "AMI"
              ? t("Ajouter une unité amie à ce dossier")
              : t("Ajouter une unité ennemie à ce dossier")
        }
        blocked={!actions.writes.canEdit || Boolean(actions.pendingZone)}
        onBlocked={actions.refuse}
        onClick={() => setOpen(true)}
      >
        <Plus size={14} /> {t("Ajouter une unité…")}
      </Button>
      {/* Au niveau de la page : le panneau animé (ordre de bataille, vue Manœuvre) retiendrait la fenêtre dans son
          cadre (position fixe relative à un parent transformé). */}
      {createPortal(
        <Presence show={open}>{open && <AddUnitDialog side={side} onClose={() => setOpen(false)} />}</Presence>,
        document.body,
      )}
    </>
  );
}

/** Ligne fixe sous la liste ennemie (docs/18 §2.2) : l'ensemble de la force forme l'ENI global de l'OPO. */
export function EniGlobalLine() {
  const { data: deployment } = useDeployment();
  const root = deployment?.composition?.opo.global;
  if (!root) return null;
  return (
    <p className="px-2 pt-1 pb-1.5 text-meta leading-snug text-ink-muted">
      {t("Ensemble de la force ({root}) — ENI global dans l'OPO", { root: unitDesignation(root) })}
    </p>
  );
}
